import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChatFilterOption, ChatLastMessage, ChatListRow, ChatPerson } from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';
import { isWithinRetention, shortAgo } from '../../utils/chat-date.util';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';
import { ChatContextMenuComponent, ChatContextMenuItem } from '../chat-context-menu/chat-context-menu.component';
import { ChatStatusMenuComponent } from '../chat-status-menu/chat-status-menu.component';

/**
 * The conversation list: search, your own status, quick filters, then every conversation.
 *
 * People and groups share **one** interleaved list ordered by attention (unread → favourites →
 * recency → the rest of the roster). They used to be separate collapsible sections, which put the
 * row you wanted in one of two places and sometimes behind a collapsed header.
 *
 * Shared by the full page and the floating launcher — `compact` trims the padding and hides the
 * status switcher for the narrower popover. Everything it renders comes from one store read, so
 * the two surfaces can never disagree about unread counts.
 */
@Component({
  selector: 'app-chat-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatAvatarComponent, ChatStatusMenuComponent, ChatContextMenuComponent],
  templateUrl: './chat-sidebar.component.html',
  styleUrls: ['./chat-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatSidebarComponent {
  readonly store = inject(ChatStore);

  /** Popover variant: tighter rows, no status switcher, no "New Group" button. */
  @Input() compact = false;

  @Output() createGroup = new EventEmitter<void>();

  readonly shortAgo = shortAgo;

  // ---- Context menu (Master Admin: hide/unhide a colleague) ------------------

  contextMenuPerson: ChatPerson | null = null;
  contextMenuX = 0;
  contextMenuY = 0;

  /**
   * Right-click on a person row. Only a Master Admin gets a menu here, and never on their own
   * row or a group's — everything else falls through to the browser's own context menu.
   */
  onRowContextMenu(event: MouseEvent, row: ChatListRow): void {
    if (row.kind !== 'person' || !this.store.amMasterAdmin || row.person.user_id === this.store.myId) {
      return;
    }
    event.preventDefault();
    this.contextMenuPerson = row.person;
    this.contextMenuX = event.clientX;
    this.contextMenuY = event.clientY;
  }

  get contextMenuItems(): ChatContextMenuItem[] {
    const hidden = this.contextMenuPerson ? this.store.isAdminHidden(this.contextMenuPerson) : false;
    return [{ id: 'toggle-hidden', label: hidden ? 'Unhide' : 'Hide from chat', icon: hidden ? 'pi-eye' : 'pi-eye-slash' }];
  }

  onContextMenuPicked(itemId: string): void {
    const person = this.contextMenuPerson;
    if (!person || itemId !== 'toggle-hidden') {
      return;
    }
    this.store.setUserHidden(person, !this.store.isAdminHidden(person));
  }

  closeContextMenu(): void {
    this.contextMenuPerson = null;
  }

  get searchTerm(): string {
    return this.store.searchTerm();
  }

  set searchTerm(value: string) {
    this.store.setSearchTerm(value);
  }

  isActive(conversationId: string | null | undefined): boolean {
    return !!conversationId && this.store.activeId() === conversationId;
  }

  // ---- Row accessors ---------------------------------------------------------
  //
  // People and groups render through one template, so each of these picks the right field for
  // whichever a row happens to be. The alternative — two near-identical `<li>` blocks — is what
  // this list had before, and it drifted: the star, the deactivated styling and the title
  // attribute all existed on one and not the other.

  nameOf(row: ChatListRow): string {
    return row.kind === 'person' ? row.person.name : row.group.name;
  }

  conversationIdOf(row: ChatListRow): string | null {
    return row.kind === 'person' ? row.person.conversation_id : row.group.conversation_id;
  }

  unreadOf(row: ChatListRow): number {
    return (row.kind === 'person' ? row.person.unread_count : row.group.unread_count) || 0;
  }

  /**
   * The row's newest message, as long as it is still inside the two-week retention window.
   *
   * An aged-out `last_message` is suppressed rather than shown: the transcript will not contain it,
   * so a row previewing text that is not in the thread it opens — with a three-week-old timestamp
   * beside it — is a row advertising a message the user cannot reach. Dropping it here also feeds
   * {@link subtitleOf}, which already draws the "no history yet" line for this case.
   */
  lastMessageOf(row: ChatListRow): ChatLastMessage | null {
    const last = (row.kind === 'person' ? row.person.last_message : row.group.last_message) ?? null;
    return last && isWithinRetention(last.created_at) ? last : null;
  }

  isInactive(row: ChatListRow): boolean {
    return row.kind === 'person' && row.person.is_active === false;
  }

  /** Only ever true for a Master Admin's own read — see {@link ChatStore.isHiddenPerson}. */
  isHidden(row: ChatListRow): boolean {
    return row.kind === 'person' && this.store.isAdminHidden(row.person);
  }

  /**
   * "Sarah is typing…" for this row, or '' when nobody is.
   *
   * Replaces the subtitle while it lasts. There are only two lines to a row, and a live signal is
   * worth more than a preview of a message the user has usually already seen.
   */
  typingOf(row: ChatListRow): string {
    // A colleague who has never spoken has no conversation yet, so nothing can be typing in one.
    const conversationId = this.conversationIdOf(row);
    return conversationId ? (this.store.typingLabelByConversation()[conversationId] ?? '') : '';
  }

  /**
   * The second line: the last message where there is one, otherwise what the row *is* — a member
   * count for a group, "Deactivated" for a colleague who has left, a role for the one master admin
   * the roster keeps. Own messages read "You: …", like Slack. A person matching none of those has
   * no second line at all, and the template's `*ngIf` keeps the empty span out of the DOM.
   */
  subtitleOf(row: ChatListRow): string {
    const last = this.lastMessageOf(row);
    if (!last) {
      if (row.kind === 'group') {
        return `${row.group.member_count} member${row.group.member_count === 1 ? '' : 's'}`;
      }
      if (this.isInactive(row)) {
        return 'Deactivated';
      }
      if (this.isHidden(row)) {
        return 'Hidden from chat';
      }
      // Only the allowlisted master admin carries a role here. Every other colleague is in the
      // list because they are a colleague, so a job title says nothing the row doesn't already —
      // for that one account the label is what explains why an admin is in a contact list at all.
      return this.store.isAllowlistedMasterAdmin(row.person) ? (row.person.role || '').trim() : '';
    }
    const prefix = last.is_own ? 'You: ' : row.kind === 'group' && last.sender_name ? `${last.sender_name.split(' ')[0]}: ` : '';
    return `${prefix}${last.preview}`;
  }

  isFavourite(row: ChatListRow): boolean {
    return row.kind === 'person' ? this.store.isFavouritePerson(row.person) : this.store.isFavouriteGroup(row.group);
  }

  openRow(row: ChatListRow): void {
    if (row.kind === 'person') {
      this.store.openPerson(row.person);
      return;
    }
    this.store.openConversation(row.group.conversation_id);
  }

  /**
   * Starring must not open the conversation — the star sits inside the row's click target, so
   * without stopping propagation every pin would also switch the thread you are reading.
   */
  toggleFavourite(event: Event, row: ChatListRow): void {
    event.stopPropagation();
    if (row.kind === 'person') {
      this.store.toggleFavouritePerson(row.person);
      return;
    }
    this.store.toggleFavouriteGroup(row.group);
  }

  /**
   * Same click-target problem as the star: this sits inside the row's own click handler, so
   * without stopping propagation a hide/unhide would also open the conversation underneath it.
   */
  toggleHidden(event: Event, row: ChatListRow): void {
    event.stopPropagation();
    if (row.kind !== 'person') {
      return;
    }
    this.store.setUserHidden(row.person, !this.store.isAdminHidden(row.person));
  }

  trackByRow = (_: number, row: ChatListRow) => `${row.kind}:${row.id}`;
  trackByFilter = (_: number, option: ChatFilterOption) => option.key;
}
